import React, { useState, useEffect } from 'react';
import { User, Mail, Phone } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const DynamicFormFields = ({ formData, onChange, disabled = false }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFields();
  }, []);

  const loadFields = async () => {
    try {
      const response = await axios.get(`${API_URL}/form-fields`);
      setFields(response.data);
    } catch (error) {
      console.error('Failed to load form fields:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (fieldType) => {
    switch (fieldType) {
      case 'email':
        return <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />;
      case 'tel':
        return <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />;
      default:
        return <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />;
    }
  };

  const handleFieldChange = (fieldName, value) => {
    onChange({
      ...formData,
      [fieldName]: value
    });
  };

  const handleCheckboxChange = (fieldName, option, checked) => {
    const currentValues = formData[fieldName] || [];
    const newValues = checked
      ? [...currentValues, option]
      : currentValues.filter(v => v !== option);
    
    handleFieldChange(fieldName, newValues);
  };

  const renderField = (field) => {
    const value = formData[field.field_name] || '';

    switch (field.field_type) {
      case 'textarea':
        return (
          <textarea
            name={field.field_name}
            value={value}
            onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
            disabled={disabled}
            required={field.is_required}
            placeholder={field.placeholder || ''}
            className="input"
            rows="3"
          />
        );

      case 'select':
        return (
          <select
            name={field.field_name}
            value={value}
            onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
            disabled={disabled}
            required={field.is_required}
            className="input"
          >
            <option value="">Select...</option>
            {field.options?.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'checkbox': {
        const selected = formData[field.field_name] || [];
        const isOther = (opt) => typeof opt === 'string' && opt.toLowerCase() === 'other';
        // "Other" is considered checked if any selected value equals "Other" OR starts with "Other:"
        const otherChecked = selected.some((v) => typeof v === 'string' && (v.toLowerCase() === 'other' || v.toLowerCase().startsWith('other:')));
        const otherText = (selected.find((v) => typeof v === 'string' && v.toLowerCase().startsWith('other:')) || '').replace(/^other:\s*/i, '');

        const toggleOther = (checked) => {
          const cleaned = selected.filter((v) => !(typeof v === 'string' && (v.toLowerCase() === 'other' || v.toLowerCase().startsWith('other:'))));
          handleFieldChange(field.field_name, checked ? [...cleaned, 'Other'] : cleaned);
        };

        const updateOtherText = (text) => {
          const cleaned = selected.filter((v) => !(typeof v === 'string' && (v.toLowerCase() === 'other' || v.toLowerCase().startsWith('other:'))));
          const newValue = text.trim() ? `Other: ${text}` : 'Other';
          handleFieldChange(field.field_name, [...cleaned, newValue]);
        };

        return (
          <div className="space-y-2">
            {field.options?.map((option, index) => {
              if (isOther(option)) {
                return (
                  <div key={index}>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={otherChecked}
                        onChange={(e) => toggleOther(e.target.checked)}
                        disabled={disabled}
                        className="rounded"
                      />
                      <span className="text-sm">Other</span>
                    </label>
                    {otherChecked && (
                      <input
                        type="text"
                        value={otherText}
                        onChange={(e) => updateOtherText(e.target.value)}
                        disabled={disabled}
                        placeholder="Please specify..."
                        className="input mt-2 ml-6"
                      />
                    )}
                  </div>
                );
              }
              return (
                <label key={index} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selected.includes(option)}
                    onChange={(e) => handleCheckboxChange(field.field_name, option, e.target.checked)}
                    disabled={disabled}
                    className="rounded"
                  />
                  <span className="text-sm">{option}</span>
                </label>
              );
            })}
          </div>
        );
      }

      case 'date':
        return (
          <input
            type="date"
            name={field.field_name}
            value={value}
            onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
            disabled={disabled}
            required={field.is_required}
            className="input"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            name={field.field_name}
            value={value}
            onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
            disabled={disabled}
            required={field.is_required}
            placeholder={field.placeholder || ''}
            className="input"
          />
        );

      default: // text, email, tel
        return (
          <div className="relative">
            {getIcon(field.field_type)}
            <input
              type={field.field_type}
              name={field.field_name}
              value={value}
              onChange={(e) => handleFieldChange(field.field_name, e.target.value)}
              disabled={disabled}
              required={field.is_required}
              placeholder={field.placeholder || ''}
              className="input pl-10"
            />
          </div>
        );
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading form fields...</div>;
  }

  // Group fields into pairs for 2-column layout where appropriate
  const renderFields = () => {
    const elements = [];
    let i = 0;

    while (i < fields.length) {
      const field = fields[i];
      const nextField = fields[i + 1];

      // Full-width fields
      if (field.field_type === 'textarea' || field.field_type === 'checkbox') {
        elements.push(
          <div key={field.id}>
            <label className="label">
              {field.field_label}
              {field.is_required && <span className="text-red-600 ml-1">*</span>}
            </label>
            {renderField(field)}
          </div>
        );
        i++;
      }
      // Two-column layout for small fields
      else if (nextField && nextField.field_type !== 'textarea' && nextField.field_type !== 'checkbox') {
        elements.push(
          <div key={`pair-${i}`} className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">
                {field.field_label}
                {field.is_required && <span className="text-red-600 ml-1">*</span>}
              </label>
              {renderField(field)}
            </div>
            <div>
              <label className="label">
                {nextField.field_label}
                {nextField.is_required && <span className="text-red-600 ml-1">*</span>}
              </label>
              {renderField(nextField)}
            </div>
          </div>
        );
        i += 2;
      }
      // Single field
      else {
        elements.push(
          <div key={field.id}>
            <label className="label">
              {field.field_label}
              {field.is_required && <span className="text-red-600 ml-1">*</span>}
            </label>
            {renderField(field)}
          </div>
        );
        i++;
      }
    }

    return elements;
  };

  return (
    <div className="space-y-4">
      {renderFields()}
      
      {/* Notes field — only show if not already covered by a custom field */}
      {!fields.some(f => f.field_name === 'notes') && (
        <div>
          <label className="label">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes || ''}
            onChange={(e) => handleFieldChange('notes', e.target.value)}
            disabled={disabled}
            placeholder="Additional information, preferences, etc."
            className="input"
            rows="3"
          />
        </div>
      )}
    </div>
  );
};

export default DynamicFormFields;
